import React, { Component, createContext } from 'react';
import createAuth0Client from '@auth0/auth0-spa-js';
import bent from 'bent';
import * as routes from '../constants/routes';
import { MixpanelConsumer } from 'react-mixpanel';

const SessionContext = createContext();
export default SessionContext;

const post = bent(process.env.REACT_APP_GRAPHQL_ENDPOINT, 'POST', 'json', 200);

// auth0 requires a full URL for logout. This method keeps us from having to hardcode the hostname in the application
const LOGOUT_URL = new URL(window.location)
LOGOUT_URL.pathname = routes.LOGOUT_TARGET
LOGOUT_URL.search = ''
LOGOUT_URL.hash = ''

export class SessionContextProvider extends Component {
  constructor(props) {
    super(props);

    this.state = {
      auth0Client: null,
      isLoading: true,
      isAuthenticated: false,
      user: null,
      idTokenClaims: null,
      household: null,
      getGraphqlClient: (idTokenClaims = this.state.idTokenClaims) => {
        const headers = {
          'Content-Type': 'application/graphql',
          Authorization: idTokenClaims.__raw,
        };
        return async (queryData) => await post('', queryData, headers)
      }
    };

    this.config = {
      domain: process.env.REACT_APP_AUTH0_DOMAIN,
      client_id: process.env.REACT_APP_AUTH0_CLIENT_ID,
      redirect_uri: window.location.origin
    };

    this.mixnpanel = null;
  }

  async componentDidMount() {
    await this.initializeAuth0();
  }

  initializeCurrentHousehold = async (idTokenClaims) => {
    const client = this.state.getGraphqlClient(idTokenClaims)
    const query = `
      query CurrentHousehold {
        myProfile {
          currentHousehold {
            householdId
            title
            schedules {
              scheduleId
              title
            }
            members {
              given_name
              family_name
              picture
            }
          }
        }
      }
    `

    const request = {
      query,
      variables: null,
      operationName: 'CurrentHousehold'
    };

    try {
      const result = await client(request)
      return result.data.myProfile.currentHousehold
    } catch(err) {
      return null;
    }
  }

  initializeThirdParty = (user) => {
    window.Appcues.identify(user.sub, {
      email: user.email,
      displayName: user.name,
      firstName: user.given_name,
      lastName: user.family_name,
      picture: user.picture,
      locale: user.locale,
    });

    this.mixpanel.identify(user.sub)
  }

  // initialize the auth0 library
  initializeAuth0 = async () => {
    const auth0Client = await createAuth0Client(this.config);
    this.setState({ auth0Client });

    // check to see if they have been redirected after login
    if (window.location.search.includes('code=') && this.handleRedirectCallback()) {
      return
    }

    const isAuthenticated = await auth0Client.isAuthenticated();
    const user = isAuthenticated ? await auth0Client.getUser() : null;
    const idTokenClaims = isAuthenticated ? await auth0Client.getIdTokenClaims() : null;
    const household = isAuthenticated ? await this.initializeCurrentHousehold(idTokenClaims) : null

    if(isAuthenticated) {
      this.initializeThirdParty(user)
    }

    this.mixpanel.track('WFH_Launched')
    this.setState({ isLoading: false, isAuthenticated, idTokenClaims, user, household });
  };

  // handle the authentication callback
  handleRedirectCallback = async () => {
    this.setState({ isLoading: true });

    try {
      await this.state.auth0Client.handleRedirectCallback();
    } catch (e) {
      console.error(e)
      let errorData = {}
      if(e.error_description) {
        errorData = { error: e.error, error_description: e.error_description }
      }
      this.mixpanel.track('WFH_Login_Error', errorData)
      return false
    }

    const user = await this.state.auth0Client.getUser();
    const idTokenClaims = await this.state.auth0Client.getIdTokenClaims();
    const household = await this.initializeCurrentHousehold(idTokenClaims)

    this.setState({ user, isAuthenticated: true, isLoading: false, household, idTokenClaims });
    window.history.replaceState({}, document.title, window.location.pathname);
    this.initializeThirdParty(user)
    this.mixpanel.track('WFH_Login_Complete')
    return true
  };

  render() {
    const { auth0Client, ...contextState } = this.state;
    const { children } = this.props;

    const config = {
      ...contextState,
      getTokenSilently: (...p) => auth0Client.getTokenSilently(...p),
      loginWithRedirect: (...p) => auth0Client.loginWithRedirect(...p),
      logout: (options = {}) => {
        options.returnTo = options.returnTo || LOGOUT_URL.toString()
        return auth0Client.logout(options)
      },
      setHousehold: (household) => this.setState({ household }),
    };

    return (
      <MixpanelConsumer>
          {mixpanel => {
            this.mixpanel = mixpanel;
            return (
              <SessionContext.Provider value={config}>
                {children}
              </SessionContext.Provider>
            )
          }}
        </MixpanelConsumer>
    );
  }
}
